import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, formatDate } from '@dataroom/shared';
import type { PlanLimitCode } from '@dataroom/shared';
import { useBillingPricing, useRooms, useTransitionAnyRoomLifecycle } from '@/api/hooks';
import { archivePackLine, planCountOf, type PlanLimitInfo } from '@/lib/plan';
import { AddArchivePackModal } from './AddArchivePackModal';
import { Modal } from './Modal';
import { UpgradePlanModal } from './UpgradePlanModal';

const TITLES: Record<PlanLimitCode, string> = {
  active_limit_reached: 'Active room limit reached',
  archived_limit_reached: 'Archived room limit reached',
  seat_limit_reached: 'Admin seat limit reached',
};

const NOUNS: Record<PlanLimitCode, string> = {
  active_limit_reached: 'active rooms',
  archived_limit_reached: 'archived rooms',
  seat_limit_reached: 'admin seats',
};

function summary(info: PlanLimitInfo): string {
  if (info.limit !== null && info.current !== null) {
    return `Your plan includes ${planCountOf(info.limit, NOUNS[info.code])} and you're using ${info.current}. Choose how to make room:`;
  }
  return `${info.detail ?? `You've reached your plan's limit on ${NOUNS[info.code]}.`} Choose how to make room:`;
}

function Option({
  title,
  hint,
  onClick,
  disabled,
}: {
  title: string;
  hint: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="w-full rounded-md border p-3 text-left hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </button>
    </li>
  );
}

/**
 * Shown instead of a raw error when create-room / archive-room / add-admin fails
 * with a plan-limit 409 (`extra.code`). Offers the way forward for that limit —
 * upgrade, add an archive pack, or free one up — and Cancel.
 */
export function PlanLimitDialog({ info, onClose }: { info: PlanLimitInfo; onClose: () => void }) {
  const navigate = useNavigate();
  const [step, setStep] = useState<'options' | 'upgrade' | 'pack' | 'delete-oldest'>('options');
  const pricing = useBillingPricing();

  if (step === 'upgrade') return <UpgradePlanModal onClose={onClose} />;
  if (step === 'pack') return <AddArchivePackModal onClose={onClose} />;
  if (step === 'delete-oldest') {
    return <DeleteOldestArchivedModal onBack={() => setStep('options')} onDone={onClose} />;
  }

  function go(path: string) {
    onClose();
    navigate(path);
  }

  return (
    <Modal
      title={TITLES[info.code]}
      description={summary(info)}
      onClose={onClose}
      footer={
        <>
          <span />
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <ul className="space-y-2">
        {info.code === 'active_limit_reached' ? (
          <>
            <Option
              title="Upgrade your plan"
              hint="Move to a plan with more active rooms."
              onClick={() => setStep('upgrade')}
            />
            <Option
              title="Archive an active room"
              hint="Open one of your active rooms and choose Archive — then try again."
              onClick={() => go('/')}
            />
          </>
        ) : null}
        {info.code === 'archived_limit_reached' ? (
          <>
            <Option
              title="Add an archive pack"
              hint={
                pricing.data
                  ? archivePackLine(pricing.data.archivePack)
                  : 'Buy extra archived-room capacity.'
              }
              onClick={() => setStep('pack')}
            />
            <Option
              title="Delete your oldest archived room"
              hint="It moves to Trash, freeing a slot."
              onClick={() => setStep('delete-oldest')}
            />
          </>
        ) : null}
        {info.code === 'seat_limit_reached' ? (
          <>
            <Option
              title="Upgrade your plan"
              hint="Move to a plan with more admin seats."
              onClick={() => setStep('upgrade')}
            />
            <Option
              title="Remove an admin"
              hint="Revoke a Rooms Admin on the Team page, or remove a room admin in a room's Settings, then try again."
              onClick={() => go('/team')}
            />
          </>
        ) : null}
      </ul>
    </Modal>
  );
}

/** Confirm, then move the tenant's oldest archived room to Trash. */
function DeleteOldestArchivedModal({ onBack, onDone }: { onBack: () => void; onDone: () => void }) {
  const rooms = useRooms();
  const trash = useTransitionAnyRoomLifecycle();
  const oldest = (rooms.data ?? [])
    .filter((r) => r.lifecycleState === 'archived')
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0];

  return (
    <Modal
      title="Delete your oldest archived room?"
      onClose={onBack}
      footer={
        <>
          {trash.error ? (
            <p className="text-sm text-destructive">{trash.error.message}</p>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onBack} disabled={trash.isPending}>
              Back
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={!oldest || trash.isPending}
              onClick={() =>
                oldest && trash.mutate({ roomId: oldest.id, to: 'trashed' }, { onSuccess: onDone })
              }
            >
              {trash.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </>
      }
    >
      {rooms.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : oldest ? (
        <p className="text-sm">
          <span className="font-medium">{oldest.name}</span> (created{' '}
          {formatDate(oldest.createdAt)}) moves to Trash for 30 days — you can
          restore it during that window (a recovery fee applies). After that it can no longer be
          restored. Once it&apos;s gone you can retry what you were doing.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">You have no archived rooms to delete.</p>
      )}
    </Modal>
  );
}
